import "./EnterpriseOrgModal.scss";
import { useState } from "react";
import { colorOptions } from "../../../../utils/colors";
import ColorPicker from "../../../ColorPicker/ColorPicker";
import useAuthStore from "../../../../store/authStore";
import { createEnterpriseOrganization } from "../../../../api/organizations/organizations";

function EnterpriseOrgModal({ onClose }) {
  const { token, fetchOrganizations, refreshUserData } = useAuthStore();
  const [organizationName, setOrganizationName] = useState("");
  const [organizationColor, setOrganizationColor] = useState(colorOptions[0]);
  const [billingContactName, setBillingContactName] = useState("");
  const [billingContactEmail, setBillingContactEmail] = useState("");
  const [billingContactPhone, setBillingContactPhone] = useState("");
  const [billingContactCompany, setBillingContactCompany] = useState("");
  const [showError, setShowError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    // Validation
    if (!organizationName.trim()) {
      setShowError("Organization name is required");
      return;
    }

    if (!billingContactName.trim() || !billingContactEmail.trim()) {
      setShowError("Billing contact name and email are required");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(billingContactEmail)) {
      setShowError("Please enter a valid email address");
      return;
    }

    setIsCreating(true);
    setShowError("");

    try {
      const orgData = {
        name: organizationName.trim(),
        color: organizationColor,
        billingContact: {
          name: billingContactName.trim(),
          email: billingContactEmail.trim(),
          phone: billingContactPhone.trim(),
          company: billingContactCompany.trim(),
        },
        billingCycle: "monthly",
      };

      await createEnterpriseOrganization(orgData, token);

      // Refresh data
      await fetchOrganizations();
      await refreshUserData();

      // Close modal
      onClose();
    } catch (error) {
      console.error("Failed to create enterprise organization:", error);
      setShowError(error.message || "Failed to create organization");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="enterprise-org-modal-overlay" onClick={onClose}>
      <div
        className="enterprise-org-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Create Enterprise Organization</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="info-banner">
            <strong>Invoice Billing:</strong> This organization will use monthly
            invoice-based billing. Members can be added without payment
            requirements. You'll receive invoices monthly based on usage.
          </div>

          {showError && <div className="error-banner">{showError}</div>}

          <div className="form-section">
            <label>Organization Name *</label>
            <input
              type="text"
              className="form-input"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder="Enter organization name"
            />
          </div>

          <div className="form-section">
            <label>Organization Color</label>
            <ColorPicker
              selectedColor={organizationColor}
              onColorSelect={setOrganizationColor}
            />
          </div>

          <div className="form-section">
            <h3>Billing Contact Information</h3>
            <label>Contact Name *</label>
            <input
              type="text"
              className="form-input"
              value={billingContactName}
              onChange={(e) => setBillingContactName(e.target.value)}
              placeholder="Name of the billing contact"
            />
          </div>

          <div className="form-section">
            <label>Contact Email *</label>
            <input
              type="email"
              className="form-input"
              value={billingContactEmail}
              onChange={(e) => setBillingContactEmail(e.target.value)}
              placeholder="billing@company.com"
            />
          </div>

          <div className="form-section">
            <label>Contact Phone</label>
            <input
              type="tel"
              className="form-input"
              value={billingContactPhone}
              onChange={(e) => setBillingContactPhone(e.target.value)}
              placeholder="Phone number"
            />
          </div>

          <div className="form-section">
            <label>Company Name</label>
            <input
              type="text"
              className="form-input"
              value={billingContactCompany}
              onChange={(e) => setBillingContactCompany(e.target.value)}
              placeholder="My organization"
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="cancel-button"
            onClick={onClose}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            className="create-button"
            onClick={handleCreate}
            disabled={isCreating}
          >
            {isCreating ? "Creating..." : "Create Enterprise Organization"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EnterpriseOrgModal;
